import { useState, useEffect } from 'react';
import { FlammableCategory } from '@components/Activity/types/FlammableCategory';
import { AffectedGroupState } from '@components/Activity/types/AffectedGroupState';
import { PreventionMeasures } from '@components/Activity/types/PreventionMeasures';
import { MitigationMeasures } from '@components/Activity/types/MitigationMeasures';
import { OrganisationalMeasures } from '@components/Activity/types/OrganisationalMeasures';
import { RiskAssessmentDTO } from '@behavior/activity/dto/RiskAssessmentDTO';
import { EnumLikelihood } from './EnumLikelihood';
import { Mode } from '@src/constants';
import { RiskAssessmentContainer } from '@behavior/activity/dto/RiskAssessmentContainer';
import { Likelihood } from '@components/Activity/types/Likelihood';
import { ContactDTO } from '@behavior/inventories/dto/ContactDTO';
import { RiskAssessmentState } from '@components/Activity/types/RiskAssessmentState';
import { WorkerTrainingInformation } from '@components/Activity/types/WorkerTrainingInformation';

type Props = {
  mode?: Mode;
};

export const useRiskAssessmentState = ({ mode }: Props) => {
  const [riskAssessment, setRiskAssessment] = useState<RiskAssessmentDTO>();
  const [otherTxt, setTextOther] = useState('');
  const [flammableCategory, setFlammableCategory] = useState({
    flammableGas: false,
    flammableLiquid: false,
    dust: false,
    other: false,
    otherTxt: otherTxt,
  });
  const categoryItems: (keyof FlammableCategory)[] = [
    'flammableGas',
    'flammableLiquid',
    'dust',
    'other',
  ];

  const [textOtherGroup, setTextOtherGroup] = useState('');
  const [affectedGroup, setAffectedGroup] = useState({
    cernStaff: false,
    cernUsers: false,
    visitors: false,
    students: false,
    contractors: false,
    other: false,
    otherTxt: textOtherGroup,
  });
  const items: (keyof AffectedGroupState)[] = [
    'cernStaff',
    'cernUsers',
    'visitors',
    'students',
    'contractors',
    'other',
  ];

  const [textOtherMeasures, setTextOtherMeasures] = useState('');
  const [preventionMeasures, setPreventionMeasures] = useState({
    limitingConcentration: false,
    inerting: false,
    ventilation: false,
    flamDetection: false,
    pce: false,
    other: false,
    otherTxt: textOtherMeasures,
  });
  const measuresItems: (keyof PreventionMeasures)[] = [
    'limitingConcentration',
    'inerting',
    'ventilation',
    'flamDetection',
    'pce',
    'other',
  ];

  const [textOtherMitigation, setTextOtherMitigation] = useState('');
  const [mitigationMeasures, setMitigationMeasures] = useState({
    explosionResistantEqp: false,
    explosionRelief: false,
    other: false,
    otherTxt: textOtherMitigation,
  });
  const mitigationItems: (keyof MitigationMeasures)[] = [
    'explosionRelief',
    'explosionResistantEqp',
    'other',
  ];

  const workerTrainingInformationItems: (keyof WorkerTrainingInformation)[] = [
    'trainingCourse',
    'other',
  ];

  const [textOtherWorkerTrainingInformation, setTextOtherWorkerTrainingInformation] = useState('');
  const [workerTrainingInformation, setWorkerTrainingInformation] = useState({
    trainingCourse: false,
    other: false,
    otherTxt: textOtherWorkerTrainingInformation,
  });

  const [leakTested, setLeakTested] = useState('');
  const [warningNotices, setWarningNotices] = useState('');
  const [esiEdms, setEsiEdms] = useState('');
  const [esiValue, setEsiValue] = useState('');

  const [organisationalMeasuresEdms, setOrganisationalMeasuresEdms] = useState('');
  const [organisationalMeasures, setOrganisationalMeasures] = useState<OrganisationalMeasures>({
    writtenInstructions: false,
    workerSupervision: false,
    workerCompetence: false,
    permitToWorkSystem: false,
    edmsOrganisationalMeasures: organisationalMeasuresEdms,
  });
  const organisationalItems: (keyof OrganisationalMeasures)[] = [
    'writtenInstructions',
    'workerSupervision',
    'workerCompetence',
    'permitToWorkSystem',
  ];

  const [additionalText, setAdditionalText] = useState('');
  const [equipmentText, setEquipmentText] = useState('');
  const [atexZone, setAtexZone] = useState('');
  const [exposedPersonnel, setExposedPersonnel] = useState<ContactDTO[]>([]);

  const [flamesPresent, setFlamesPresent] = useState(`${EnumLikelihood.HIGH}`);
  const [hotSurfacePresent, setHotSurfacePresent] = useState(`${EnumLikelihood.HIGH}`);
  const [sparksPresent, setSparksPresent] = useState(`${EnumLikelihood.HIGH}`);
  const [chemReactionPresent, setChemReactionPresent] = useState(`${EnumLikelihood.HIGH}`);
  const [electricalApparatusPresent, setElectricalApparatusPresent] = useState(
    `${EnumLikelihood.HIGH}`
  );
  const [electricityPresent, setElectricityPresent] = useState(`${EnumLikelihood.HIGH}`);
  const [electromagneticRadiationPresent, setElectromagneticRadiationPresent] = useState(
    `${EnumLikelihood.HIGH}`
  );
  const [ionisingRadiationPresent, setIonisingRadiationPresent] = useState(
    `${EnumLikelihood.HIGH}`
  );
  const [otherPresent, setOtherPresent] = useState(`${EnumLikelihood.HIGH}`);

  const [flamesIgnition, setFlamesIgnition] = useState(`${EnumLikelihood.HIGH}`);
  const [hotSurfaceIgnition, setHotSurfaceIgnition] = useState(`${EnumLikelihood.HIGH}`);
  const [sparksIgnition, setSparksIgnition] = useState(`${EnumLikelihood.HIGH}`);
  const [chemReactionIgnition, setChemReactionIgnition] = useState(`${EnumLikelihood.HIGH}`);
  const [electricalApparatusIgnition, setElectricalApparatusIgnition] = useState(
    `${EnumLikelihood.HIGH}`
  );
  const [electricityIgnition, setElectricityIgnition] = useState(`${EnumLikelihood.HIGH}`);
  const [electromagneticRadiationIgnition, setElectromagneticRadiationIgnition] = useState(
    `${EnumLikelihood.HIGH}`
  );
  const [ionisingRadiationIgnition, setIonisingRadiationIgnition] = useState(
    `${EnumLikelihood.HIGH}`
  );
  const [otherIgnition, setOtherIgnition] = useState(`${EnumLikelihood.HIGH}`);
  const [otherTable, setOtherTable] = useState(false);
  const [otherSource, setOtherSource] = useState('');
  const [riskAssessmentContainer, setRiskAssessmentContainer] = useState<RiskAssessmentContainer>();
  const [sourcePresent, setSourcePresent] = useState<Likelihood>();
  const [ignitionLikelihood, setIgnitionLikelihood] = useState<Likelihood>();
  const [disabled, setDisabled] = useState(true);

  useEffect(() => {
    if (mode == Mode.EDIT || mode == Mode.VIEW) {
      const updatedIgnitionLikelihood: Likelihood = {
        ...ignitionLikelihood,
        other: otherTable,
      };

      setIgnitionLikelihood(updatedIgnitionLikelihood);
      const updatedSourcePresent: Likelihood = {
        ...sourcePresent,
        other: otherTable,
      };

      setSourcePresent(updatedSourcePresent);
    }
  }, [otherTable]);

  useEffect(() => {
    const updatedSourcePresent = {
      ...sourcePresent,
      flames: flamesPresent,
      hotSurface: hotSurfacePresent,
      sparks: sparksPresent,
      chemReaction: chemReactionPresent,
      electricalApparatus: electricalApparatusPresent,
      electricity: electricityPresent,
      electromagneticRadiation: electromagneticRadiationPresent,
      ionisingRadiation: ionisingRadiationPresent,
      other: otherTable,
      otherTxt: otherSource,
      otherPresent: otherPresent,
    };

    setSourcePresent(updatedSourcePresent);
  }, [
    flamesPresent,
    hotSurfacePresent,
    sparksPresent,
    chemReactionPresent,
    electricalApparatusPresent,
    electricityPresent,
    electromagneticRadiationPresent,
    ionisingRadiationPresent,
    otherPresent,
    otherSource,
  ]);

  useEffect(() => {
    const updatedIgnitionLikelihood = {
      ...ignitionLikelihood,
      flames: flamesIgnition,
      hotSurface: hotSurfaceIgnition,
      sparks: sparksIgnition,
      chemReaction: chemReactionIgnition,
      electricalApparatus: electricalApparatusIgnition,
      electricity: electricityIgnition,
      electromagneticRadiation: electromagneticRadiationIgnition,
      ionisingRadiation: ionisingRadiationIgnition,
      other: otherTable,
      otherTxt: otherSource,
      otherIgnition: otherIgnition,
    };

    setIgnitionLikelihood(updatedIgnitionLikelihood);
  }, [
    flamesIgnition,
    hotSurfaceIgnition,
    sparksIgnition,
    chemReactionIgnition,
    electricalApparatusIgnition,
    electricityIgnition,
    electromagneticRadiationIgnition,
    ionisingRadiationIgnition,
    otherIgnition,
    otherSource,
  ]);

  useEffect(() => {
    const updatedFlammableCategory = {
      ...flammableCategory,
      otherTxt: otherTxt,
    };
    setFlammableCategory(updatedFlammableCategory);
  }, [otherTxt]);

  useEffect(() => {
    const updatedAffectedGroup = {
      ...affectedGroup,
      otherTxt: textOtherGroup,
    };
    setAffectedGroup(updatedAffectedGroup);
  }, [textOtherGroup]);

  useEffect(() => {
    const updatedPreventionMeasures = {
      ...preventionMeasures,
      otherTxt: textOtherMeasures,
    };
    setPreventionMeasures(updatedPreventionMeasures);
  }, [textOtherMeasures]);

  useEffect(() => {
    const updatedMitigationMeasures = {
      ...mitigationMeasures,
      otherTxt: textOtherMitigation,
    };
    setMitigationMeasures(updatedMitigationMeasures);
  }, [textOtherMitigation]);

  useEffect(() => {
    const updatedWorkerTrainingInformation = {
      ...workerTrainingInformation,
      otherTxt: textOtherWorkerTrainingInformation,
    };
    setWorkerTrainingInformation(updatedWorkerTrainingInformation);
  }, [textOtherWorkerTrainingInformation]);

  useEffect(() => {
    const updatedOrganisationalMeasures = {
      ...organisationalMeasures,
      edmsOrganisationalMeasures: organisationalMeasuresEdms,
    };
    setOrganisationalMeasures(updatedOrganisationalMeasures);
  }, [organisationalMeasuresEdms]);

  useEffect(() => {
    const updatedRiskAssessmentContainer: RiskAssessmentContainer = {
      ...riskAssessmentContainer,
      additionalInf: additionalText,
      workEquipmentAndMaterialsInfo: equipmentText,
      hasWarningNotices: warningNotices,
      leakTested: leakTested,
      edmsAtexZone: atexZone,
      electricalSafetyInspectionPerformed: esiValue,
      edmsESI: esiEdms,
      mitigationMeasures: mitigationMeasures,
      affectedGroup: affectedGroup,
      preventionMeasures: preventionMeasures,
      sourcePresent: sourcePresent,
      ignitionLikelihood: ignitionLikelihood,
      organisationalMeasures: organisationalMeasures,
      flammableCategory: flammableCategory,
      workerTrainingInformation: workerTrainingInformation,
    };
    setRiskAssessmentContainer(updatedRiskAssessmentContainer);
  }, [
    additionalText,
    equipmentText,
    warningNotices,
    leakTested,
    sourcePresent,
    atexZone,
    mitigationMeasures,
    affectedGroup,
    preventionMeasures,
    ignitionLikelihood,
    organisationalMeasures,
    flammableCategory,
    exposedPersonnel,
    esiValue,
    workerTrainingInformation,
  ]);

  return {
    riskAssessment,
    setRiskAssessment,
    otherTxt,
    setTextOther,
    flammableCategory,
    setFlammableCategory,
    categoryItems,
    affectedGroup,
    setAffectedGroup,
    items,
    textOtherGroup,
    setTextOtherGroup,
    preventionMeasures,
    setPreventionMeasures,
    textOtherMeasures,
    setTextOtherMeasures,
    measuresItems,
    mitigationMeasures,
    setMitigationMeasures,
    textOtherMitigation,
    setTextOtherMitigation,
    mitigationItems,
    leakTested,
    setLeakTested,
    warningNotices,
    setWarningNotices,
    esiEdms,
    setEsiEdms,
    esiValue,
    setEsiValue,
    organisationalMeasures,
    setOrganisationalMeasures,
    organisationalItems,
    organisationalMeasuresEdms,
    setOrganisationalMeasuresEdms,
    additionalText,
    setAdditionalText,
    equipmentText,
    setEquipmentText,
    atexZone,
    setAtexZone,
    exposedPersonnel,
    setExposedPersonnel,
    flamesPresent,
    setFlamesPresent,
    hotSurfacePresent,
    setHotSurfacePresent,
    sparksPresent,
    setSparksPresent,
    chemReactionPresent,
    setChemReactionPresent,
    electricalApparatusPresent,
    setElectricalApparatusPresent,
    electricityPresent,
    setElectricityPresent,
    electromagneticRadiationPresent,
    setElectromagneticRadiationPresent,
    ionisingRadiationPresent,
    setIonisingRadiationPresent,
    otherPresent,
    setOtherPresent,
    flamesIgnition,
    setFlamesIgnition,
    hotSurfaceIgnition,
    setHotSurfaceIgnition,
    sparksIgnition,
    setSparksIgnition,
    chemReactionIgnition,
    setChemReactionIgnition,
    electricalApparatusIgnition,
    setElectricalApparatusIgnition,
    electricityIgnition,
    setElectricityIgnition,
    electromagneticRadiationIgnition,
    setElectromagneticRadiationIgnition,
    ionisingRadiationIgnition,
    setIonisingRadiationIgnition,
    otherIgnition,
    setOtherIgnition,
    otherTable,
    setOtherTable,
    riskAssessmentContainer,
    setRiskAssessmentContainer,
    setOtherSource,
    otherSource,
    disabled,
    setDisabled,
    sourcePresent,
    setSourcePresent,
    ignitionLikelihood,
    setIgnitionLikelihood,
    workerTrainingInformationItems,
    workerTrainingInformation,
    setWorkerTrainingInformation,
    setTextOtherWorkerTrainingInformation,
    textOtherWorkerTrainingInformation,
  } as RiskAssessmentState;
};
